import { TRIAL_CREDITS, CREDIT_PACKAGES, credits, money } from "@/lib/billing";
import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Code2,
  Frame,
  Layers,
  MousePointer2,
  PlugZap,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";
import { CanvasDemo } from "./canvas-demo";
import { Action, ClosingCTA, CodeBlock, FAQ, PageIntro } from "./ui";

const capabilities = [
  {
    icon: Frame,
    number: "01",
    title: "A canvas for your thinking.",
    text: "Go from a blank frame to a considered interface. Arrange screens, shape layouts, and see the whole picture.",
  },
  {
    icon: PlugZap,
    number: "02",
    title: "Another pair of hands.",
    text: "Connect your agent through MCP or the API. Let it create and refine the same elements you work with.",
  },
  {
    icon: SlidersHorizontal,
    number: "03",
    title: "Every detail stays yours.",
    text: "Move a layer. Change the type. Find the right spacing. Keep shaping the work until it feels right.",
  },
];

export function HomePage() {
  return (
    <>
      <section className="mk-hero mk-container">
        <div className="mk-hero-heading">
          <a className="mk-release" href="/changelog">
            <span /> Meet Proximaforma v0.2 <ArrowRight size={13} />
          </a>
          <h1>
            Your ideas.
            <br />A new <span>form.</span>
          </h1>
        </div>
        <div className="mk-hero-copy">
          <span className="mk-eyebrow">People + agents + one canvas</span>
          <p>
            A design workspace where your ideas take shape, with a little help
            from your AI agents.
          </p>
          <div className="mk-actions">
            <Action />
            <a className="mk-text-link" href="/product">
              Explore the product <ArrowUpRight size={16} />
            </a>
          </div>
          <span className="mk-fine">
            Register. Connect your agent. Review together.
          </span>
        </div>
      </section>
      <section
        className="mk-hero-demo mk-container"
        aria-label="Interactive product preview"
      >
        <CanvasDemo />
        <div className="mk-demo-under">
          <span>From first thought to thoughtful interface.</span>
          <span>HUMAN DIRECTION. AGENT ITERATION.</span>
        </div>
      </section>
      <section className="mk-capabilities mk-container">
        {capabilities.map(({ icon: Icon, number, title, text }) => (
          <article key={number}>
            <div>
              <Icon size={23} strokeWidth={1.5} />
              <span>{number}</span>
            </div>
            <h2>{title}</h2>
            <p>{text}</p>
          </article>
        ))}
      </section>
      <section className="mk-agent-section">
        <div className="mk-container mk-agent-grid">
          <div>
            <span className="mk-eyebrow">Built to work together</span>
            <h2>
              You set the direction.
              <br />
              Your agent helps
              <br />
              <em>move it forward.</em>
            </h2>
            <p>
              Describe a screen. Ask for a variation. Refine it by hand. Your
              agent works with real layers and frames, so every change stays
              open to your next idea.
            </p>
            <Action href="/integrations">Bring your agent</Action>
            <div className="mk-protocols">
              <span>MCP</span>
              <span>REST API</span>
              <span>Live updates</span>
            </div>
          </div>
          <div className="mk-agent-example">
            <div className="mk-agent-prompt">
              <span>
                <MousePointer2 size={14} /> YOU
              </span>
              <p>
                “Add a welcome screen with a clear headline and a button to get
                started.”
              </p>
            </div>
            <div className="mk-agent-reply">
              <span>
                <Code2 size={15} /> YOUR AGENT · EXAMPLE OPERATION
              </span>
              <pre>
                {
                  'batch_edit({\n  projectId: "your-project",\n  operations: [\n    { op: "add", element: {\n      id: "welcome", type: "frame",\n      name: "Welcome", width: 400,\n      height: 600\n    }},\n    // Add a headline and a button…\n  ]\n})'
                }
              </pre>
            </div>
            <div className="mk-agent-result">
              <Layers size={16} />
              <span>Editable elements. Ready for your touch.</span>
              <Check size={15} />
            </div>
          </div>
        </div>
      </section>
      <section className="mk-workflow mk-container">
        <div className="mk-section-heading">
          <div>
            <span className="mk-eyebrow">A natural back-and-forth</span>
            <h2>
              Think it. Shape it.
              <br />
              Make it better.
            </h2>
          </div>
          <a className="mk-text-link" href="/use-cases">
            Find your way to work <ArrowUpRight size={17} />
          </a>
        </div>
        <div className="mk-workflow-steps">
          {[
            [
              "Start with a possibility",
              "An empty canvas or an editable sample. Give your product idea a place to begin.",
            ],
            [
              "Build on it together",
              "Let an agent add the first elements. Take over to tune the layout and details.",
            ],
            [
              "Take the work forward",
              "Preview your screens and export SVG or structured JSON for the next step.",
            ],
          ].map(([title, text], i) => (
            <article key={title}>
              <span>0{i + 1}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>
      <FAQ />
      <ClosingCTA />
    </>
  );
}

const productFeatures = [
  {
    icon: Frame,
    label: "01 / Make space",
    title: "One idea. As many frames as it needs.",
    body: "Create desktop screens, mobile layouts, and interface variations side by side. Pan, zoom, and fit the canvas to see how the pieces relate.",
    items: [
      "Frames with nested elements",
      "Desktop and mobile sample designs",
      "Preview individual screens",
    ],
  },
  {
    icon: SlidersHorizontal,
    label: "02 / Get it right",
    title: "Big picture, meet small details.",
    body: "Pin feedback exactly where it belongs. People and agents can reply in the same thread, track what is new, and mark a comment handled when the work is done.",
    items: [
      "Pinned comments with colleague and agent replies",
      "New, In progress, and Handled statuses",
      "Search and filters for status, page, author, and mentions",
      "Viewer and commenter invitations",
    ],
  },
  {
    icon: PlugZap,
    label: "03 / Build together",
    title: "The same canvas, another perspective.",
    body: "Agents inspect and edit the live design through MCP or the REST API. Changes arrive in your browser, with an activity history that shows who changed what.",
    items: [
      "Named, revocable agent keys",
      "Atomic batches and revision checks",
      "Shared comment threads and statuses",
    ],
  },
  {
    icon: Layers,
    label: "04 / Keep going",
    title: "Your next step is an open one.",
    body: "Save your work locally, revisit it later, or export a portable design document. Take an SVG into your next visual workflow, or use JSON in your own tools.",
    items: [
      "Private accounts and durable storage",
      "SVG and JSON exports",
      "Native design chat and open agent connections",
    ],
  },
];

export function ProductPage() {
  return (
    <>
      <PageIntro
        eyebrow="The workspace"
        title={
          <>
            Space to explore.
            <br />
            Tools to be precise.
          </>
        }
      >
        A visual home for your next product idea. Work directly on the canvas,
        invite an agent into the process, and keep every element editable.
      </PageIntro>
      <div className="mk-container">
        <CanvasDemo />
      </div>
      <section className="mk-feature-list mk-container">
        {productFeatures.map(({ icon: Icon, label, title, body, items }) => (
          <article key={label}>
            <div className="mk-feature-symbol">
              <Icon size={52} strokeWidth={1} />
              <span>{label}</span>
            </div>
            <div>
              <h2>{title}</h2>
              <p>{body}</p>
              <ul className="mk-check-list">
                {items.map((item) => (
                  <li key={item}>
                    <Check size={15} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </section>
      <section className="mk-note mk-container">
        <span className="mk-eyebrow">A clear starting point</span>
        <h2>Made for interface design.</h2>
        <p>
          This first version uses positioned elements and fixed text boxes. Auto
          layout, reusable component instances, interactive prototypes, and
          application code generation are not available yet.
        </p>
        <a href="/changelog" className="mk-text-link">
          See what’s in this release <ArrowUpRight size={15} />
        </a>
      </section>
      <ClosingCTA />
    </>
  );
}

const workflows = [
  {
    label: "Explore a product",
    audience: "FOR FOUNDERS & PRODUCT THINKERS",
    title: "Make the idea tangible.",
    body: "Move an idea out of your notes and into a screen you can discuss. Explore a few directions, compare them side by side, and decide what deserves another pass.",
    steps: [
      "Start with a blank project",
      "Ask your agent for the essential screens",
      "Edit the details and export a concept",
    ],
    prompt:
      "Create a new project for a habit-tracking app. Add a 390 × 844 home frame with a greeting, three habit cards, and a clear button to add a habit. Use named layers and keep everything editable.",
  },
  {
    label: "Refine a screen",
    audience: "FOR DESIGNERS",
    title: "Keep your eye on the details.",
    body: "Use an agent for a first arrangement or a specific change, then bring your own judgment to typography, composition, and spacing. Every layer stays in reach.",
    steps: [
      "Open or create a screen",
      "Ask for a targeted variation",
      "Tune type, spacing, and color by hand",
    ],
    prompt:
      "Read the current project and its revision. Add a second version of the main screen beside the original, using new element IDs. Give the heading more room and simplify the button labels. Keep the original screen intact.",
  },
  {
    label: "Build a workflow",
    audience: "FOR DEVELOPERS & AGENT BUILDERS",
    title: "Give your tools a visual output.",
    body: "Turn structured requests into visual elements with the API. Let a local agent read a design, apply a batch of changes, and export the result for your next step.",
    steps: [
      "Connect with MCP or an API key",
      "Read the document before you edit",
      "Apply a batch and inspect the result",
    ],
    prompt:
      "List the projects and read the project I select. Use its latest revision to add a settings screen with grouped labels, input-like rectangles, and a save button. Make the edits in one atomic batch, then export the SVG.",
  },
];

export function UseCasesPage() {
  const [selected, setSelected] = useState(0);
  const workflow = workflows[selected];
  return (
    <>
      <PageIntro
        eyebrow="Ways to work"
        title={
          <>
            A thought starter.
            <br />A detail finder.
            <br />
            Your kind of tool.
          </>
        }
      >
        From the first product conversation to the next design iteration, make
        room for the way you like to work.
      </PageIntro>
      <section className="mk-workflow-explorer mk-container">
        <div className="mk-choice-tabs" aria-label="Choose a workflow">
          {workflows.map((item, index) => (
            <button
              key={item.label}
              aria-pressed={index === selected}
              onClick={() => setSelected(index)}
            >
              {item.label}
              <ArrowUpRight size={15} />
            </button>
          ))}
        </div>
        <div className="mk-use-case" aria-live="polite">
          <div>
            <span className="mk-eyebrow">{workflow.audience}</span>
            <h2>{workflow.title}</h2>
            <p>{workflow.body}</p>
            <ol>
              {workflow.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <Action />
          </div>
          <div>
            <span className="mk-eyebrow">A prompt to get you started</span>
            <CodeBlock
              value={workflow.prompt}
              label="Prompt for your connected agent"
            />
            <p className="mk-fine">
              Ask Forma in the Design agent window, or connect your own agent.
              These are example prompts; the result depends on the agent you
              use.
            </p>
            <a className="mk-text-link" href="/docs/mcp">
              Connect an agent <ArrowUpRight size={15} />
            </a>
          </div>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}

export function IntegrationsPage() {
  return (
    <>
      <PageIntro
        eyebrow="Bring your agents"
        title={
          <>
            Your tools.
            <br />A shared canvas.
          </>
        }
      >
        Connect the agent you already use. Give it a place to read, create, and
        refine visual designs alongside you.
      </PageIntro>
      <section className="mk-integration-options mk-container">
        <article>
          <span className="mk-eyebrow">01 / Model Context Protocol</span>
          <PlugZap size={40} strokeWidth={1.3} />
          <h2>
            Give an agent
            <br />a set of design tools.
          </h2>
          <p>
            Thirteen tools for projects, elements, vectors, batches, and
            exports. Connect a local MCP client with stdio, or use the
            Streamable HTTP endpoint with an agent key.
          </p>
          <ul className="mk-check-list">
            <li>
              <Check size={15} />
              Read the complete element tree
            </li>
            <li>
              <Check size={15} />
              Add, edit, delete, and reorder elements
            </li>
            <li>
              <Check size={15} />
              Export the result as SVG
            </li>
          </ul>
          <Action href="/connect/chatgpt">Add to ChatGPT</Action>
          <p><a href="/docs/mcp">Set up another MCP client</a></p>
        </article>
        <article>
          <span className="mk-eyebrow">02 / REST API</span>
          <Code2 size={40} strokeWidth={1.3} />
          <h2>
            Make the canvas
            <br />
            part of your workflow.
          </h2>
          <p>
            Use familiar HTTP requests to create projects and edit designs.
            Structured documents and atomic batches make it straightforward to
            build your own tooling.
          </p>
          <ul className="mk-check-list">
            <li>
              <Check size={15} />
              JSON requests and responses
            </li>
            <li>
              <Check size={15} />
              Revision checks for concurrent edits
            </li>
            <li>
              <Check size={15} />A downloadable OpenAPI reference
            </li>
          </ul>
          <Action href="/docs/api" secondary>
            Explore the API
          </Action>
        </article>
      </section>
      <section className="mk-connection-steps mk-container">
        <div>
          <span className="mk-eyebrow">From setup to first shape</span>
          <h2>
            Three steps.
            <br />
            One workspace.
          </h2>
        </div>
        <ol>
          {[
            ["Open the app", "Keep Proximaforma running on your computer."],
            [
              "Connect your agent",
              "Copy setup instructions from Connect agent and paste them into your agent. It names itself and connects.",
            ],
            [
              "Make your first edit",
              "Ask your agent to list projects, read a design, and add an element. Watch it appear on the canvas.",
            ],
          ].map(([title, text]) => (
            <li key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </li>
          ))}
        </ol>
      </section>
      <section className="mk-note mk-container">
        <ShieldCheck size={28} strokeWidth={1.5} />
        <h2>Access you can see and manage.</h2>
        <p>
          Choose which canvases each agent can access, set read, comment, or
          design permissions, and revoke its key when you’re done. Keys expire
          automatically. Your agent provider handles its own model access and
          billing.
        </p>
        <a className="mk-text-link" href="/privacy">
          Understand your data <ArrowUpRight size={15} />
        </a>
      </section>
      <ClosingCTA />
    </>
  );
}

export function PricingPage() {
  return (
    <>
      <PageIntro
        eyebrow="Pricing"
        title={
          <>
            A little less setup.
            <br />A little more making.
          </>
        }
      >
        Start with {credits(TRIAL_CREDITS)} free Forma credits. Add prepaid credit when you
        need more, with usage priced at model cost plus our markup.
      </PageIntro>
      <section className="mk-pricing mk-container">
        <div className="mk-price-card">
          <div className="mk-price-top">
            <span className="mk-eyebrow">Available now</span>
            <span className="mk-price-badge">PRODUCT PREVIEW</span>
          </div>
          <h2>{credits(TRIAL_CREDITS)} credits to begin.</h2>
          <p>Your account, private canvases, comments, and Forma design agent.</p>
          <Action />
          <span className="mk-fine">
            One allowance per account. Shared across text and voice.
          </span>
          <ul className="mk-check-list">
            {[
              "Private canvases and page navigation",
              "Blank projects and editable sample designs",
              "MCP server and REST API",
              "Live agent edits and comment threads",
              "SVG and JSON exports",
              "Reviewer invitations and scoped agent access",
            ].map((feature) => (
              <li key={feature}>
                <Check size={16} />
                {feature}
              </li>
            ))}
          </ul>
        </div>
        <div className="mk-pricing-context">
          <span className="mk-eyebrow">What to know</span>
          <h2>A working product preview.</h2>
          <p>
            This installation runs on your own computer. Create an account,
            connect your agent, and invite reviewers on an accessible
            deployment.
          </p>
          <dl>
            <div>
              <dt>Prepaid credit packages</dt>
              <dd>
                {CREDIT_PACKAGES.map((pack) => (
                  <p key={pack.id}>{credits(pack.credits)} Forma credits · {money(pack.amountCents / 100, pack.currency)}</p>
                ))}
                £1 = 100 credits. Local currency prices are shown by Stripe at checkout.
              </dd>
            </div>
            <div>
              <dt>Workspace limits</dt>
              <dd>
                100 canvases per owner · No fixed element-count limit · 200
                operations per batch.
              </dd>
            </div>
            <div>
              <dt>Design-agent usage</dt>
              <dd>
                Your remaining Forma credits update after each AI response. Credits are Proximaforma usage units, separate from model tokens.
                Text and voice share the same balance; trial credit is used first.
                The purchase panel shows this installation’s markup before checkout.
              </dd>
            </div>
            <div>
              <dt>Refill when you need it</dt>
              <dd>
                Buy Forma credits through Stripe. Choose a supported local currency or GBP at checkout; your credit amount stays the same.
                No subscription or automatic top-ups. External agents connected
                through MCP or the API use their own provider billing.
              </dd>
            </div>
          </dl>
          <a className="mk-text-link" href="/docs">
            Get started locally <ArrowUpRight size={16} />
          </a>
        </div>
      </section>
      <FAQ />
      <ClosingCTA />
    </>
  );
}

export function ChangelogPage() {
  return (
    <>
      <PageIntro
        eyebrow="What’s new"
        title={
          <>
            A work in progress.
            <br />
            By design.
          </>
        }
      >
        A record of what’s available, as the workspace takes shape.
      </PageIntro>
      <section className="mk-release-log mk-container">
        <aside>
          <span className="mk-price-badge">v0.2.0</span>
          <p>Accounts and shared review</p>
        </aside>
        <article>
          <h2>A canvas for your whole conversation.</h2>
          <p>
            Register for private canvases, connect agents with scoped access,
            and invite reviewers. A simpler canvas interface puts pages and
            comments in floating panels.
          </p>
          <ul>
            <li>Pinned comments, replies, statuses, search, and filters.</li>
            <li>Agent access to comment threads through MCP and the API.</li>
            <li>
              View-only and commenting invitations, key expiry, revocation, and
              recovery codes.
            </li>
          </ul>
          <p>
            This product preview runs locally. Public hosting, email delivery,
            paid plans, and public ChatGPT directory availability depend on deployment setup.
          </p>
        </article>
      </section>
      <section className="mk-release-log mk-container">
        <aside>
          <span className="mk-price-badge">v0.1.0</span>
          <p>Initial local release</p>
        </aside>
        <article>
          <h2>The first shared canvas.</h2>
          <p>
            Proximaforma brings visual editing, a REST API, and an MCP server
            into one local design workspace.
          </p>
          {[
            [
              "The canvas",
              "Frames, text, shapes, buttons, and images. Nested layers, a property inspector, drag and resize, preview, undo and redo, and SVG or JSON export.",
            ],
            [
              "Your agents, connected",
              "MCP over stdio and Streamable HTTP, thirteen design tools, named agent keys, live changes, and an activity panel.",
            ],
            [
              "Work that stays with you",
              "SQLite persistence, atomic editing batches, and revision checks that reject stale changes.",
            ],
            [
              "A clearer view",
              "The canvas repaints when zooming to keep text and shapes sharp instead of enlarging a cached image.",
            ],
            [
              "Vector drawing",
              "Pen and line tools, polygon and star presets, editable anchors and Bézier handles, stroke styles, and three dedicated MCP vector tools.",
            ],
            [
              "A home around the workspace",
              "Product and use-case pages, integration guides, pricing information, searchable documentation, and a data overview.",
            ],
          ].map(([title, text]) => (
            <div key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
          <a href="/docs" className="mk-text-link">
            Explore the documentation <ArrowUpRight size={16} />
          </a>
        </article>
      </section>
      <ClosingCTA />
    </>
  );
}

export function PrivacyPage() {
  return (
    <>
      <PageIntro eyebrow="Privacy" title={<>Your workspace.<br />A clear view of your data.</>}>
        How ProximaForma handles accounts, designs, and conversations.
      </PageIntro>
      <article className="mk-prose mk-container mk-data-page">
        <p>Updated 18 September 2026. ProximaForma at proximaforma.com is operated by RatioSky LTD. For privacy requests, contact <a href="mailto:support@proximaforma.com">support@proximaforma.com</a>.</p>
        <h2>Information we store</h2>
        <p>We store your account name and email, password and recovery-code hashes, project memberships, designs, uploaded references, comments, activity, agent conversations, generated images, and usage and billing records. We use this information to provide your workspace, control access, meter paid features, and investigate service problems.</p>
        <h2>Hosting and service providers</h2>
        <p>The hosted service uses Amazon Web Services in Frankfurt, Germany for its application database and object storage. Content is delivered through a global content delivery network. AWS also processes operational logs, backups, and verification emails. Support messages are handled through Microsoft 365. Payments are processed by Stripe; we retain payment references and credit records, while payment-card details are handled by Stripe.</p>
        <h2>AI conversations and generated images</h2>
        <p>When you use Forma, your messages and the canvas information its tools read are sent to OpenAI for text, voice, or image generation. A voice conversation sends microphone audio while the call is active. We save conversation text, but do not save audio recordings. Generated images are stored with your workspace. OpenAI processes requests under its applicable service terms and data policies.</p>
        <h2>Connected apps and agents</h2>
        <p>A connected agent can read or change only the canvases and actions allowed by your connection and account membership. Information it reads is shared with the service operating that agent. When you consent to an email identity scope, we share your verified email address with the connected app, including for enterprise email-domain restrictions. Verification requires a code sent to your email; an account profile alone does not establish ownership.</p>
        <p>You can inspect and revoke agent access in Connect agent. OAuth access tokens expire after one hour; new connections renew automatically until you revoke access. Revocation prevents further access, but does not erase information already received by another service.</p>
        <h2>Browser storage and credentials</h2>
        <p>An HTTP-only session cookie keeps you signed in for up to seven days. Browser storage remembers workspace preferences. This build does not include advertising or third-party marketing tracking scripts. Passwords use salted hashes, and session tokens, recovery codes, invitation tokens, and API keys are stored as hashes. Verification codes expire after ten minutes and permit at most five attempts. Design exports do not include agent credentials.</p>
        <h2>External resources</h2>
        <p>Linked images and fonts may be requested from their original providers. Those providers receive normal request information, such as your IP address. An exported SVG can retain external image links.</p>
        <h2>Retention and deletion</h2>
        <p>Workspace data is retained while you use the service, unless you remove it or request deletion. Deleting a canvas removes it from the workspace and schedules cleanup of its related database records. Immutable files and older object versions can remain in storage until separate cleanup; contact us for a complete deletion request. Operational logs and scheduled database backups have a fourteen-day retention period. Billing and transaction records are retained separately where needed for accounting and resolving payment issues.</p>
        <h2>Your choices and requests</h2>
        <p>You can export designs as JSON or SVG, delete canvases, and revoke connected agents. Contact <a href="mailto:support@proximaforma.com">support@proximaforma.com</a> to request access, correction, or deletion of account data, or to ask about retention. We may need to verify your identity before acting on an account request.</p>
        <h2>Self-hosted installations</h2>
        <p>If you run the software yourself, its operator controls storage, backups, and connected providers. The local default uses a SQLite database in the installation’s data directory. This page describes the service at proximaforma.com.</p>
        <a href="/support" className="mk-text-link">Contact support <ArrowUpRight size={15} /></a>
      </article>
    </>
  );
}

export function SupportPage() {
  return (
    <>
      <PageIntro eyebrow="Support" title={<>Help with<br />your workspace.</>}>
        Account, connection, billing, and privacy support for ProximaForma.
      </PageIntro>
      <article className="mk-prose mk-container mk-data-page">
        <h2>Contact us</h2>
        <p>Email <a href="mailto:support@proximaforma.com">support@proximaforma.com</a>. Include what you were trying to do, the approximate time, and the error you saw. Share only the design content needed to explain the issue. Never send passwords, recovery codes, verification codes, or agent keys.</p>
        <h2>Connecting ChatGPT</h2>
        <p>Start with the <a href="/connect/chatgpt">ChatGPT connection guide</a>. Sign in to ProximaForma, review the requested access, select Give access, and return to the connection window. If asked to verify your email, enter the code sent to your account address. Connections can be revoked from Connect agent in the workspace.</p>
        <h2>Accounts and billing</h2>
        <p>For an account or payment issue, include your account email and, if relevant, the receipt reference. Do not include payment-card details. We may ask you to verify ownership before changing account information.</p>
        <h2>Privacy requests</h2>
        <p>Use the same support address for data access, correction, and deletion requests. Our <a href="/privacy">privacy page</a> explains the information we store and the services that process it.</p>
        <a href="/docs" className="mk-text-link">Browse documentation <ArrowUpRight size={15} /></a>
      </article>
    </>
  );
}

export function NotFoundPage() {
  return (
    <>
      <PageIntro
        eyebrow="404 / A little off-canvas"
        title={
          <>
            This page hasn’t
            <br />
            taken shape.
          </>
        }
      >
        The address may have changed, or the page may not exist. Your workspace
        is one click away.
      </PageIntro>
      <div className="mk-container mk-not-found-actions">
        <Action />
        <Action href="/" secondary>
          Back to the homepage
        </Action>
        <a className="mk-text-link" href="/docs">
          Browse the docs <ArrowUpRight size={15} />
        </a>
      </div>
    </>
  );
}
